import { createContext, useContext, useEffect, useMemo, useState } from "react";
import ApiRequest from "./ApiRequest";
import { ApiEndpoints } from "./ApiEndpoints";

const endpoints = ApiEndpoints.rbac;

// Loads the logged-in user's permission slugs once and shares them across the
// sidebar and the route guard.
//
// status: "loading" | "ready"
// permissionSlugs: Set<string> | null  (null = no restrictions -> fail open,
// e.g. super admin or when permissions cannot be determined)
const PermissionContext = createContext({
  status: "loading",
  permissionSlugs: null,
  hasPermission: () => true,
});

export const usePermissions = () => useContext(PermissionContext);

export function PermissionProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [permissionSlugs, setPermissionSlugs] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const finish = (slugs) => {
      if (cancelled) return;
      setPermissionSlugs(slugs || null);
      setStatus("ready");
    };

    // Fallback: derive the logged-in user's permission slugs from the existing
    // /users + /roles endpoints (both return 200). Used when
    // api/rbac/my-permissions is missing or errors (e.g. 404 on the backend).
    const deriveFromRoles = async () => {
      try {
        const email = localStorage.getItem("email");
        if (!email) return finish(null);

        const [usersResponse, rolesResponse] = await Promise.all([
          ApiRequest("GET", endpoints.userList),
          ApiRequest("GET", endpoints.roleList),
        ]);

        const user = (usersResponse.users || []).find((u) => u.email === email);
        if (!user || !user.role_id) return finish(null);

        const role = (rolesResponse.roles || []).find((r) => r._id === user.role_id);
        if (!role) return finish(null);

        // Super admin keeps access to everything (fail open)
        if (/super[\s_\-]*admin/i.test(role.name || "")) return finish(null);

        const slugs = (role.permissions || []).map((p) => p.slug).filter(Boolean);
        console.warn("[RBAC] my-permissions unavailable, derived slugs from roles:", slugs);
        finish(slugs.length ? new Set(slugs) : null);
      } catch (error) {
        // Fail open: keep everything accessible if permissions cannot be determined
        finish(null);
      }
    };

    ApiRequest("GET", endpoints.myPermissions)
      .then((response) => {
        if (response && !response.error && (response.permissions || []).length) {
          finish(new Set(response.permissions.map((p) => p.slug)));
        } else {
          deriveFromRoles();
        }
      })
      .catch(() => deriveFromRoles());

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      status,
      permissionSlugs,
      hasPermission: (slug) =>
        status !== "ready" ? true : permissionSlugs ? permissionSlugs.has(slug) : true,
    }),
    [status, permissionSlugs]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}
